/**
 * Drawer Header Component
 * 
 * Reusable header component for drawers with title, description, and close button.
 */

import * as React from "react";
import { Drawer } from "@mantine/core";
import { cn } from "@/lib/utils.js";
import type { DrawerConfig } from "../types.js";

interface DrawerHeaderProps {
  config: DrawerConfig;
}

export const DrawerHeader: React.FC<DrawerHeaderProps> = ({ config }) => {
  const isBottomDrawer = config.side === "bottom";
  const isPillOnly = isBottomDrawer && !config.title && !config.description && !config.showCloseButton;

  // When there's nothing to show but the pill, skip the Mantine header entirely.
  // The pill will be rendered by the content layer (e.g. MobileDrawerSearchWrapper)
  // so the content can start right at the top without a dead-space strip.
  if (isPillOnly) {
    return null;
  }

  if (!config.title && !config.description && !isBottomDrawer) {
    return null;
  }

  return (
    <Drawer.Header className="relative">
      {/* Swipe handle pill — standard mobile sheet UX for bottom drawers */}
      {isBottomDrawer && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-gray-300" />
      )}
      <div className="flex flex-col gap-1">
        {config.title && <Drawer.Title>{config.title}</Drawer.Title>}
        {config.description && (
          <p className="text-sm text-muted-foreground">{config.description}</p>
        )}
      </div>
      {config.showCloseButton && <Drawer.CloseButton />}
    </Drawer.Header>
  );
};
